/** Router de la aplicación: arma rutas dinámicas desde el menú del usuario y el registry de componentes, con login, selección de sucursal, 404 y redirección inicial. */
import { useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useUIStore } from "@/stores/uiStore";
import Login from "@/views/login";
import NotFound from "@/views/404";
import SelectWorkspace from "@/views/selectworkspace";
import MainLayout from "../mainLayout/MainLayout";
import PrivateRoute from "../router/PrivateRoute";
import { getComponent } from "./componentRegistry";
import LazyRoute from "./LazyRoute";
import { getDefaultPath } from "@/utils";
import { esSuperAdmin } from "@/domain";
import { tieneMultiplesEmpresas } from "@/stores/authStore";
import { useWorkSession } from "@/hooks";

const AppRouter = () => {
  const { loggedUserInnerwork, activeSucursal, workspaceResuelto } = useWorkSession();
  const currentModule = useUIStore((s) => s.currentModule);
  const itemsMenu = useUIStore((s) => s.itemsMenu);

  const getRedirectPath = (): string => {
    const sucursales = loggedUserInnerwork?.sucursales || [];
    const tieneSucursalValida =
      !!activeSucursal && sucursales.some((s) => s.idSucursal === activeSucursal.idSucursal);

    // Si el usuario ya resolvió su workspace (eligió sucursal o decidió seguir sin una), no se
    // lo vuelve a mandar al wizard aunque no haya sucursal activa.
    const necesitaElegirWorkspace =
      !workspaceResuelto &&
      // El caso "sin empresa y sin sucursales" sólo aplica al superadmin; un usuario común sin
      // sucursales (estado que no debería ocurrir) pasa directo en lugar de quedar en el wizard.
      ((esSuperAdmin(loggedUserInnerwork) &&
        !loggedUserInnerwork.codEmpresa &&
        sucursales.length === 0) ||
        // Con más de una empresa hay que elegir empresa (y después sucursal o seguir sin una).
        (tieneMultiplesEmpresas(loggedUserInnerwork) && !loggedUserInnerwork.codEmpresa) ||
        (sucursales.length > 1 && !tieneSucursalValida));

    if (necesitaElegirWorkspace) {
      return "/select-workspace";
    }

    return getDefaultPath(itemsMenu, currentModule);
  };

  const dynamicRoutes = useMemo(() => {
    return itemsMenu
      .filter((item) => getComponent(item.nombreComponente))
      .map((item) => ({
        path: `/${item.pathProceso}`,
        nombreComponente: item.nombreComponente,
      }));
  }, [itemsMenu]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            Object.keys(loggedUserInnerwork).length > 0 ? (
              <Navigate to={getRedirectPath()} replace />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/select-workspace"
          element={<PrivateRoute element={<SelectWorkspace />} loggedUser={loggedUserInnerwork} />}
        />

        {dynamicRoutes.map(({ path, nombreComponente }) => {
          const Component = getComponent(nombreComponente)!;
          return (
            <Route
              key={path}
              path={path}
              element={
                <PrivateRoute
                  element={
                    <MainLayout>
                      <LazyRoute component={Component} />
                    </MainLayout>
                  }
                  loggedUser={loggedUserInnerwork}
                />
              }
            />
          );
        })}

        <Route
          path="*"
          element={
            Object.keys(loggedUserInnerwork).length > 0 ? <NotFound /> : <Navigate to="/" replace />
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
